import { z } from 'zod';

// ─── Auth Schemas ────────────────────────────────────────────────

export const registerSchema = z.object({
  email: z
    .string()
    .email('Email non valida')
    .max(255, 'Email troppo lunga')
    .transform((v) => v.toLowerCase().trim()),
  password: z
    .string()
    .min(8, 'La password deve avere almeno 8 caratteri')
    .max(128, 'La password deve avere massimo 128 caratteri')
    .regex(/[A-Z]/, 'La password deve contenere almeno una lettera maiuscola')
    .regex(/[0-9]/, 'La password deve contenere almeno un numero'),
  name: z
    .string()
    .min(1, 'Il nome è obbligatorio')
    .max(100, 'Il nome deve avere massimo 100 caratteri')
    .trim(),
  referralCode: z.string().max(12).optional(),
});

export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email: z
    .string()
    .email('Email non valida')
    .transform((v) => v.toLowerCase().trim()),
  password: z.string().min(1, 'La password è obbligatoria'),
});

export type LoginInput = z.infer<typeof loginSchema>;

export const forgotPasswordSchema = z.object({
  email: z
    .string()
    .email('Email non valida')
    .transform((v) => v.toLowerCase().trim()),
});

export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z.object({
  token: z.string().min(1, 'Token mancante'),
  password: z
    .string()
    .min(8, 'La password deve avere almeno 8 caratteri')
    .max(128, 'La password deve avere massimo 128 caratteri')
    .regex(/[A-Z]/, 'La password deve contenere almeno una lettera maiuscola')
    .regex(/[0-9]/, 'La password deve contenere almeno un numero'),
});

export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

// ─── Monitoraggio Schemas ────────────────────────────────────────

export const createMonitoraggioSchema = z
  .object({
    aeroportoPartenza: z
      .string()
      .length(3, "Il codice aeroporto deve essere di 3 caratteri")
      .toUpperCase(),
    aeroportoArrivo: z
      .string()
      .length(3, "Il codice aeroporto deve essere di 3 caratteri")
      .toUpperCase(),
    tipoMonitoraggio: z.enum(['fisso', 'flessibile']).default('fisso'),

    // For 'fisso' type (existing fields)
    dataAndata: z.coerce.date().optional(),
    dataRitorno: z.coerce
      .date()
      .nullable()
      .optional(),
    soloAndata: z.boolean().optional().default(false),

    // For 'flessibile' type (new fields)
    periodoInizio: z.coerce.date().optional(),
    periodoFine: z.coerce.date().optional(),
    soggiornoMin: z.number().int().min(1).max(30).nullable().optional(),
    soggiornoMax: z.number().int().min(1).max(30).nullable().optional(),

    budgetMax: z
      .number()
      .positive('Il budget deve essere un numero positivo')
      .nullable()
      .optional(),

    classeCabina: z.enum(['economy', 'premium_economy', 'business', 'first']).nullable().optional(),
    passeggeri: z.number().int().min(1).max(6).optional().default(1),
  })
  .refine(
    (data) => data.aeroportoPartenza !== data.aeroportoArrivo,
    {
      message: "L'aeroporto di partenza e arrivo devono essere diversi",
      path: ['aeroportoArrivo'],
    }
  )
  .refine(
    (data) => {
      if (data.tipoMonitoraggio === 'fisso') {
        return data.dataAndata != null;
      } else {
        return data.periodoInizio != null && data.periodoFine != null;
      }
    },
    {
      message: 'Date richieste per il tipo di monitoraggio selezionato',
    }
  )
  .refine(
    (data) => {
      if (data.tipoMonitoraggio === 'fisso' && data.dataAndata) {
        return data.dataAndata > new Date();
      }
      return true;
    },
    {
      message: 'La data di andata deve essere futura',
      path: ['dataAndata'],
    }
  )
  .refine(
    (data) => {
      if (data.dataRitorno && data.dataAndata) {
        return data.dataRitorno > data.dataAndata;
      }
      return true;
    },
    {
      message: 'La data di ritorno deve essere successiva alla data di andata',
      path: ['dataRitorno'],
    }
  )
  .refine(
    (data) => {
      if (data.tipoMonitoraggio === 'flessibile' && data.periodoInizio && data.periodoFine) {
        const diffDays = Math.ceil(
          (data.periodoFine.getTime() - data.periodoInizio.getTime()) / (1000 * 60 * 60 * 24)
        );
        return diffDays >= 1 && diffDays <= 30;
      }
      return true;
    },
    {
      message: 'Il periodo flessibile deve essere tra 1 e 30 giorni',
    }
  )
  .refine(
    (data) => {
      if (data.soggiornoMin != null && data.soggiornoMax != null) {
        return data.soggiornoMin <= data.soggiornoMax;
      }
      return true;
    },
    { message: 'Il soggiorno minimo deve essere minore o uguale al massimo', path: ['soggiornoMin'] },
  );

export type CreateMonitoraggioInput = z.infer<typeof createMonitoraggioSchema>;

export const updateMonitoraggioSchema = z
  .object({
    aeroportoPartenza: z
      .string()
      .length(3, "Il codice aeroporto deve essere di 3 caratteri")
      .toUpperCase()
      .optional(),
    aeroportoArrivo: z
      .string()
      .length(3, "Il codice aeroporto deve essere di 3 caratteri")
      .toUpperCase()
      .optional(),
    tipoMonitoraggio: z.enum(['fisso', 'flessibile']).optional(),
    dataAndata: z.coerce.date().optional(),
    dataRitorno: z.coerce.date().nullable().optional(),
    soloAndata: z.boolean().optional(),
    periodoInizio: z.coerce.date().optional(),
    periodoFine: z.coerce.date().optional(),
    soggiornoMin: z.number().int().min(1).max(30).nullable().optional(),
    soggiornoMax: z.number().int().min(1).max(30).nullable().optional(),
    budgetMax: z.number().positive('Il budget deve essere un numero positivo').nullable().optional(),
    attivo: z.boolean().optional(),
    notificheAttive: z.boolean().optional(),
    classeCabina: z.enum(['economy', 'premium_economy', 'business', 'first']).nullable().optional(),
    passeggeri: z.number().int().min(1).max(6).optional(),
  })
  .refine(
    (data) => {
      if (data.aeroportoPartenza && data.aeroportoArrivo) {
        return data.aeroportoPartenza !== data.aeroportoArrivo;
      }
      return true;
    },
    {
      message: "L'aeroporto di partenza e arrivo devono essere diversi",
      path: ['aeroportoArrivo'],
    }
  )
  .refine(
    (data) => {
      if (data.tipoMonitoraggio === 'flessibile' && data.periodoInizio && data.periodoFine) {
        const diffDays = Math.ceil(
          (data.periodoFine.getTime() - data.periodoInizio.getTime()) / (1000 * 60 * 60 * 24)
        );
        return diffDays >= 1 && diffDays <= 30;
      }
      return true;
    },
    {
      message: 'Il periodo flessibile deve essere tra 1 e 30 giorni',
    }
  )
  .refine(
    (data) => {
      if (data.soggiornoMin != null && data.soggiornoMax != null) {
        return data.soggiornoMin <= data.soggiornoMax;
      }
      return true;
    },
    { message: 'Il soggiorno minimo deve essere minore o uguale al massimo', path: ['soggiornoMin'] },
  );

export type UpdateMonitoraggioSchemaInput = z.infer<typeof updateMonitoraggioSchema>;

// ─── User Schemas ────────────────────────────────────────────────

const SUPPORTED_LOCALES = ['it', 'en', 'fr', 'pt', 'es', 'ru', 'zh', 'ar'] as const;

export const updateUserSchema = z.object({
  name: z
    .string()
    .max(100, 'Il nome deve avere massimo 100 caratteri')
    .trim()
    .optional(),
  locale: z
    .enum(SUPPORTED_LOCALES, { errorMap: () => ({ message: 'Lingua non supportata' }) })
    .optional(),
});

export type UpdateUserSchemaInput = z.infer<typeof updateUserSchema>;

// ─── Pagination Schema ───────────────────────────────────────────

export const paginationSchema = z.object({
  page: z.coerce
    .number()
    .int('La pagina deve essere un numero intero')
    .min(1, 'La pagina minima è 1')
    .default(1),
  pageSize: z.coerce
    .number()
    .int('La dimensione della pagina deve essere un numero intero')
    .min(1, 'La dimensione minima è 1')
    .max(100, 'La dimensione massima è 100')
    .default(20),
});

export type PaginationInput = z.infer<typeof paginationSchema>;

// ─── Checkout Schema ─────────────────────────────────────────────

export const checkoutSessionSchema = z.object({
  plan: z.enum(['premium', 'unlimited'], {
    errorMap: () => ({ message: 'Piano non valido' }),
  }),
  successUrl: z.string().url('URL di successo non valido'),
  cancelUrl: z.string().url('URL di annullamento non valido'),
});

export type CheckoutSessionSchemaInput = z.infer<typeof checkoutSessionSchema>;

// ─── Admin Schemas ──────────────────────────────────────────────

export const adminUpdateUserSchema = z.object({
  plan: z.enum(['free', 'premium', 'unlimited']).optional(),
  role: z.enum(['user', 'admin', 'super_admin']).optional(),
  banned: z.boolean().optional(),
});

export type AdminUpdateUserInput = z.infer<typeof adminUpdateUserSchema>;

export const createDiscountCodeSchema = z.object({
  code: z.string().min(3).max(50).transform((s) => s.toUpperCase()),
  description: z.string().max(500).optional(),
  discountPercent: z.number().min(1).max(100),
  maxUses: z.number().int().positive().nullable().optional(),
  validFrom: z.string().datetime(),
  validUntil: z.string().datetime(),
  applicablePlans: z.array(z.enum(['premium', 'unlimited'])).min(1),
});

export type CreateDiscountCodeInput = z.infer<typeof createDiscountCodeSchema>;

export const updateDiscountCodeSchema = z.object({
  description: z.string().max(500).optional(),
  discountPercent: z.number().min(1).max(100).optional(),
  maxUses: z.number().int().positive().nullable().optional(),
  validFrom: z.string().datetime().optional(),
  validUntil: z.string().datetime().optional(),
  applicablePlans: z.array(z.enum(['premium', 'unlimited'])).min(1).optional(),
  active: z.boolean().optional(),
});

export type UpdateDiscountCodeInput = z.infer<typeof updateDiscountCodeSchema>;

export const errorLogFilterSchema = z.object({
  source: z.enum(['api', 'worker-monitor', 'worker-ai', 'notification']).optional(),
  severity: z.enum(['error', 'warning', 'critical']).optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
});

export type ErrorLogFilterInput = z.infer<typeof errorLogFilterSchema>;
